import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { FeatureCollection } from 'geojson';
import { featureCollection } from '@turf/turf';
import {
  createAoi,
  getFuzzyLogicInterpretation,
  getInterpretationDescription,
  getInterpretationRating,
  getReport,
  getReportDescription,
  getThematicMap,
  getThematicMapLegend,
  getThematicMapRating,
} from '../utils/soilDataAccess';
import { MAP_UNIT_DESCRIPTION } from '../constants/soil-attributes/mapUnitDescription';
import {
  ColorKey,
  LegendItem,
  SoilAttribute,
  SoilAttributeData,
} from '../types/soilAttributes';

export interface SoilAttributesState {
  activeAttribute: SoilAttribute;
  activeData: Nullable<SoilAttributeData>;
  colorKey: Nullable<ColorKey>;
  legend: Nullable<LegendItem[]>;
  loadSoilAttribute: (soilAttribute: SoilAttribute) => Promise<void>;
  resetSoilAttributes: () => void;
  setActiveAttribute: (soilAttribute: SoilAttribute) => void;
  soilAttributes: { [name: string]: SoilAttributeData };
}

export const initialState = {
  activeAttribute: MAP_UNIT_DESCRIPTION,
  activeData: null,
  colorKey: null,
  legend: null,
  loadSoilAttribute: () => Promise.resolve(),
  resetSoilAttributes: () => {},
  setActiveAttribute: () => {},
  soilAttributes: {},
};

const initialReducerState = {};

interface SoilAttributesData {
  [name: string]: SoilAttributeData;
}

interface ResetSoilAction {
  type: 'reset';
}

interface UpsertSoilAction {
  type: 'add' | 'update';
  soilAttrData: SoilAttributeData;
}

type SoilReducerAction = ResetSoilAction | UpsertSoilAction;

const soilAttrsDataReducer = (
  state: SoilAttributesData,
  action: SoilReducerAction,
): SoilAttributesData => {
  switch (action.type) {
    case 'add': {
      return {
        ...state,
        [action.soilAttrData.name]: action.soilAttrData,
      };
    }
    case 'update': {
      return {
        ...state,
        [action.soilAttrData.name]: {
          ...state[action.soilAttrData.name],
          ...action.soilAttrData,
        },
      };
    }
    case 'reset': {
      return initialReducerState;
    }
    default:
      throw new Error(`Invalid type: ${action}.`);
  }
};

const useSoilAttributes = ({
  data = featureCollection([]),
}: {
  data: FeatureCollection;
}): SoilAttributesState => {
  const aoiPromiseRef = useRef<Nullable<Promise<Nullable<string>>>>(null);
  const requestPromiseMap = useRef<{
    [key: string]: Nullable<Promise<void>>;
  }>({});
  const [soilAttributes, dispatch] = useReducer(
    soilAttrsDataReducer,
    initialReducerState,
  );
  const soilAttributesRef = useRef<SoilAttributesData>(soilAttributes);
  soilAttributesRef.current = soilAttributes;
  const [aoiId, setAoiId] = useState<Nullable<string>>(null);
  const [activeAttribute, setActiveAttribute] = useState<SoilAttribute>(
    MAP_UNIT_DESCRIPTION,
  );
  const activeData = soilAttributes[activeAttribute?.name] || null;

  const resetSoilAttributes = () => {
    dispatch({ type: 'reset' });
    setAoiId(null);
  };

  const getAoiPromise = () => {
    // We already have a pending AOI creation request
    if (aoiPromiseRef.current) {
      return aoiPromiseRef.current;
    }
    // We have a non-null AOI ID already
    if (aoiId != null) {
      return Promise.resolve(aoiId);
    }
    if (!data?.features.length) {
      return Promise.resolve(null);
    }
    aoiPromiseRef.current = createAoi(data)
      .then((result: Nullable<string>) => {
        setAoiId(result);
        return result;
      })
      .finally(() => {
        aoiPromiseRef.current = null;
      });
    return aoiPromiseRef.current;
  };

  const loadSoilAttribute = (soilAttribute: SoilAttribute) => {
    const soilAttrData = soilAttributes[soilAttribute.name];
    if (soilAttrData) return Promise.resolve();
    dispatch({
      type: 'add',
      soilAttrData: { name: soilAttribute.name, loading: true },
    });

    const requestPromise = getAoiPromise()
      .then((aoiid) =>
        Promise.all([
          getFuzzyLogicInterpretation(data, soilAttribute.fuzzyLogicRuleName),
          getInterpretationRating(
            aoiid,
            soilAttribute.interpRatingShortFormData,
          ),
          getReport(aoiid, soilAttribute.reportShortFormData),
        ]),
      )
      .then(([fuzzyLogicIntepretation, interpresultid, report]) =>
        Promise.all([
          Promise.resolve(fuzzyLogicIntepretation),
          getInterpretationDescription(soilAttribute.attributeKey),
          getThematicMap(interpresultid),
          getThematicMapLegend(interpresultid),
          getThematicMapRating(interpresultid),
          getReportDescription(soilAttribute.reportId),
          Promise.resolve(report),
        ]),
      )
      .then(
        ([
          fuzzyLogicInterpretation,
          description,
          thematicMap,
          thematicMapLegend,
          thematicMapRating,
          reportDescription,
          report,
        ]) => {
          dispatch({
            type: 'update',
            soilAttrData: {
              loading: false,
              name: soilAttribute.name,
              description,
              fuzzyLogicInterpretation,
              report,
              reportDescription,
              thematicMap,
              thematicMapLegend,
              thematicMapRating,
            },
          });
        },
      )
      .finally(() => {
        dispatch({
          type: 'update',
          soilAttrData: { loading: false, name: soilAttribute.name },
        });
        requestPromiseMap.current[soilAttribute.name] = null;
      });
    requestPromiseMap.current[soilAttribute.name] = requestPromise;
    return requestPromise;
  };

  useEffect(() => {
    if (activeData || !data?.features.length) return;
    loadSoilAttribute(activeAttribute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAttribute]);

  const colorKey = useMemo(() => {
    if (activeData?.thematicMapRating) {
      return Object.fromEntries(
        activeData.thematicMapRating.Table.map(
          ([, , mapUnitKey, , , rgbString]: string[]) => [
            mapUnitKey,
            rgbString,
          ],
        ),
      );
    }
    if (activeAttribute?.getColorKey) {
      return activeAttribute.getColorKey({
        data,
        report: activeData?.report,
      });
    }
    return null;
  }, [activeAttribute, activeData, data]);

  const legend = useMemo(() => {
    if (activeData?.thematicMapLegend) {
      return activeData.thematicMapLegend.Table.map(
        ([, , , label, hexCode]: string[]) => ({ label, hexCode }),
      );
    }
    if (activeAttribute?.getLegend) {
      return activeAttribute.getLegend({
        data,
        report: activeData?.report,
      });
    }
    return null;
  }, [activeAttribute, activeData, data]);

  return {
    activeAttribute,
    activeData,
    colorKey,
    legend,
    loadSoilAttribute,
    resetSoilAttributes,
    setActiveAttribute,
    soilAttributes,
  };
};

export default useSoilAttributes;
